import React from 'react';
import { Button, Modal } from '@keplerco/core';

interface PlanFeature {
  name: string;
  free: boolean;
  pro: boolean;
  enterprise: boolean;
}

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Paywall modal component
 * Displays pricing information and feature comparison when a user attempts to access a premium feature
 */
export function PaywallModal({ isOpen, onClose }: PaywallModalProps): JSX.Element {
  // Features list for comparison table
  const features: PlanFeature[] = [
    { name: 'Roles & Skills Taxonomy', free: true, pro: true, enterprise: true },
    { name: 'AI-Powered Generation', free: true, pro: true, enterprise: true },
    { name: 'Up to 5 users', free: true, pro: true, enterprise: true },
    { name: 'Team Invitations', free: false, pro: true, enterprise: true },
    { name: 'Skills Assessments', free: false, pro: true, enterprise: true },
    { name: 'Advanced Analytics', free: false, pro: true, enterprise: true },
    { name: 'Custom Branding', free: false, pro: false, enterprise: true },
    { name: 'Dedicated Support', free: false, pro: false, enterprise: true },
  ];

  return (
    <Modal 
      open={isOpen} 
      onClose={onClose}
      size="large"
    >
      <h2 style={{ padding: '16px 24px', borderBottom: '1px solid var(--borders-1)', margin: 0 }}>Upgrade to Access Premium Features</h2>
      <div style={{ padding: '24px' }}>
        <p style={{ marginBottom: '24px' }}>
          You're currently on the Free plan. Upgrade to Pro to unlock team invites, assessments, and advanced analytics.
        </p>
        
        <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
          {/* Free Plan */}
          <div style={{ flex: 1, border: '1px solid var(--borders-2)', borderRadius: '8px', padding: '16px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '8px' }}>Free</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px' }}>$0</div>
            <div style={{ color: 'var(--accent-3)', marginBottom: '16px' }}>
              Build your taxonomy and explore the platform
            </div>
            <Button type="button" disabled style={{ width: '100%' }}>
              Current Plan
            </Button>
          </div>
          
          
          {/* Enterprise Plan */}
          <div style={{ flex: 1, border: '1px solid var(--borders-2)', borderRadius: '8px', padding: '16px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '8px' }}>Pro & Enterprise</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '16px' }}>Scale</div>
            <div style={{ color: 'var(--accent-3)', marginBottom: '16px' }}>
              Full customization and dedicated support
            </div>
            <Button type="button" onClick={() => window.open('https://keplerco.io/contact', '_blank')} style={{ width: '100%' }}>
              Contact Sales
            </Button>
          </div>
        </div>
        
        {/* Features Comparison Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 16px', borderBottom: '1px solid var(--borders-2)' }}>Feature</th>
                <th style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-2)' }}>Free</th>
                <th style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-2)' }}>Pro</th>
                <th style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-2)' }}>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={index}>
                  <td style={{ padding: '8px 16px', borderBottom: '1px solid var(--borders-1)' }}>{feature.name}</td>
                  <td style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-1)' }}>
                    {feature.free ? '✓' : '–'}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-1)' }}>
                    {feature.pro ? '✓' : '–'}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 16px', borderBottom: '1px solid var(--borders-1)' }}>
                    {feature.enterprise ? '✓' : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--accent-3)', fontSize: '0.9rem' }}>
            Need help deciding? Contact us at <a href="mailto:sales@keplerco.io">sales@keplerco.io</a>
          </p>
        </div>
      </div>
    </Modal>
  );
}
